import { App, Empty, Flex, FormInstance } from "antd";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Point, Route, Schedule } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { ScheduleFiltersPanel, ScheduleMenuConfig } from "@/domains/route";
import { useAppSelector } from "@/hooks";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { BlockLoader } from "@/shared/ui/BlockLoader";

import RouteEditorForm, {
    RouteEditorFormValues,
} from "../../components/RouteEditorForm";
import { ScheduleList } from "../../components/ScheduleList";
import { useScheduleSelection } from "../../hooks";
import { RouteEditorCapabilities } from "../../RouteEditor.policy";
import {
    RouteEditorModal,
    RouteEditorModalState,
} from "../../RouteEditor.types";

export type RouteEditorEditMainContentBlockProps = {
    capabilities: RouteEditorCapabilities;
    currentRoute: Route;
    schedulesLoading: boolean;
    saveLoading: boolean;
    isDeleteBlocked: boolean;
    points: Record<string, Point>;
    routeEditorForm: FormInstance<RouteEditorFormValues>;
    selection: ReturnType<typeof useScheduleSelection>;
    onSave: (force?: boolean) => Promise<void>;
    onSetModalState: (state: RouteEditorModalState) => void;
};

export const RouteEditorEditMainContentBlock = ({
    capabilities,
    currentRoute,
    schedulesLoading,
    saveLoading,
    isDeleteBlocked,
    points,
    routeEditorForm,
    selection,
    onSave,
    onSetModalState,
}: RouteEditorEditMainContentBlockProps) => {
    const navigate = useNavigate();
    const { notification } = App.useApp();

    const currentScheduleList = useAppSelector(
        (s) => s.manager.currentScheduleList,
    );

    const canDelete =
        !isDeleteBlocked || !capabilities.isDeleteRestrictedByPlan;

    const showDeleteBlockedNotification = useCallback(() => {
        notification.warning({
            title: "Видалення заблоковане",
            description: "План на територію вже затверджено",
            duration: 4,
        });
    }, [notification]);

    const handleAddPointClick = useCallback(() => {
        navigate(
            buildRoutePath(ROUTES.RouteManagementAddPoint, {
                routeId: currentRoute.uuid,
            }),
            {
                state: {
                    formValues: routeEditorForm.getFieldsValue(),
                    isDeleteBlocked,
                },
            },
        );
    }, [currentRoute.uuid, isDeleteBlocked, routeEditorForm, navigate]);

    const scheduleMenuConfig: ScheduleMenuConfig[] = useMemo(
        () => [
            {
                type: "view",
                label: "Переглянути ТТ",
                onClick: (item: Schedule) => {
                    navigate(
                        buildRoutePath(ROUTES.PointDetails, {
                            pointId: item.pointId,
                        }),
                    );
                },
            },
            {
                type: "edit",
                label: "Редагувати",
                onClick: (item) => {
                    onSetModalState({
                        type: RouteEditorModal.EditSchedule,
                        payload: {
                            schedule: item,
                            createMode: false,
                            allSchedules: currentScheduleList,
                            route: currentRoute,
                        },
                    });
                },
            },
            ...(capabilities.canMoveSchedules
                ? [
                      {
                          type: "move" as const,
                          label: "Перемістити",
                          onClick: (item: Schedule) => {
                              onSetModalState({
                                  type: RouteEditorModal.MoveSchedule,
                                  payload: {
                                      pointIds: [item.pointId],
                                      currentRouteId: currentRoute.uuid,
                                  },
                              });
                          },
                      },
                  ]
                : []),
            {
                type: "delete",
                label: "Видалити",
                onClick: ({ pointId }) => {
                    if (!canDelete) {
                        showDeleteBlockedNotification();
                        return;
                    }

                    if (points[pointId]?.hasDebt) {
                        onSetModalState({
                            type: RouteEditorModal.WarningDebt,
                            payload: {
                                isMultiSelect:
                                    selection.selectedSchedules.length > 1,
                            },
                        });
                        return;
                    }

                    onSetModalState({
                        type: RouteEditorModal.DeleteSchedule,
                        payload: {
                            pointIds: [pointId],
                            isEmptyAfterDelete:
                                currentScheduleList.length === 1,
                            createMode: false,
                            currentRouteId: currentRoute.uuid,
                        },
                    });
                },
            },
        ],
        [
            canDelete,
            capabilities.canMoveSchedules,
            currentRoute,
            currentScheduleList,
            points,
            onSetModalState,
            selection.selectedSchedules.length,
            navigate,
            showDeleteBlockedNotification,
        ],
    );

    return (
        <MainContainer $paddingTop="0" $paddingBottom="8px">
            <Flex
                vertical
                gap={16}
                style={{
                    overflowY: "auto",
                    paddingTop: 16,
                    height: "100%",
                }}
            >
                {!selection.multiSelectMode && (
                    <RouteEditorForm
                        form={routeEditorForm}
                        showAgentField={capabilities.canEditAgent}
                    />
                )}
                <ScheduleFiltersPanel form={selection.filtersForm} />

                {schedulesLoading ? (
                    <BlockLoader />
                ) : selection.sortedAndFilteredSchedules?.length ? (
                    <ScheduleList
                        displaySchedules={selection.sortedAndFilteredSchedules}
                        multiSelectMode={selection.multiSelectMode}
                        selectedSchedules={selection.selectedSchedules}
                        handleSelectSchedule={selection.handleSelectSchedule}
                        handleTap={selection.enterMultiSelectMode}
                        dayFilter={selection.watchedFilters?.day ?? ""}
                        scheduleMenuConfig={scheduleMenuConfig}
                        points={points}
                    />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Немає активних точок"
                    />
                )}
            </Flex>

            <Flex vertical gap={8} style={{ paddingTop: 8, marginTop: "auto" }}>
                <BaseButton
                    type="default"
                    onClick={handleAddPointClick}
                    disabled={saveLoading}
                >
                    Додати ТТ
                </BaseButton>
                <BaseButton
                    type="primary"
                    onClick={() => void onSave()}
                    loading={saveLoading}
                >
                    Зберегти
                </BaseButton>
            </Flex>
        </MainContainer>
    );
};
